import { mapValues as mapV } from 'lodash';
import { flow, isEmpty, pickBy, mapValues } from 'lodash/fp';

import { formatNumber } from 'utils/number-formats';

/**
 * takes a string representing the bin label and a bin size, and returns
 * the label of the bin range.
 *
 * @param  {String} label           label of the bin (representing the lower bound)
 * @param  {Number} binSize         bin size (numeric)
 * @param  {Number} channelCustoms  customisations for the channel
 * @return {String}                 string in the form "lower - upper"
 */
function convertToRangeLabel(label, binSize, channelCustoms) {
  const lower = parseFloat(label);
  const upper = formatNumber(lower + binSize, channelCustoms);
  return `${formatNumber(lower, channelCustoms)} - ${upper}`;
}

/**
 * converts quantitatively binned labels (e.g. "90") into a string representing
 * the lower and upper bound of the bin (e.g. "90 - 120").
 *
 * @param  {Object} stageData  object containing documents returned from the agg pipeline & channels object in the metadata
 * @return {Object}            object containing documents with the transformed labels.
 */
function binDocumentsQuantitative(stageData) {
  const {
    environment: { channels, customisations },
  } = stageData;

  // creates an object of all channels mapped to their quantitative binning
  const binnedChannels = flow(
    pickBy(encoding => encoding.isBinning && encoding.quantitativeBinning),
    mapValues(encoding => encoding.quantitativeBinning)
  )(channels);

  // return early if there are no changes to the documents required
  if (isEmpty(binnedChannels)) {
    return {};
  }

  const documents = stageData.documents.map(document => {
    return mapV(document, (value, channel) => {
      const binning = binnedChannels[channel];
      if (binning) {
        return {
          raw: value,
          display: convertToRangeLabel(
            document[channel],
            binning.binSize,
            customisations.channels[channel]
          ),
        };
      }
      return value;
    });
  });

  return { documents };
}

export default {
  input: [
    'documents',
    'environment.channels.*.isBinning',
    'environment.channels.*.quantitativeBinning',
    'environment.customisations.channels.*.number*',
  ],
  fn: binDocumentsQuantitative,
};
export { binDocumentsQuantitative, convertToRangeLabel };
